import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { focusApi, taskApi } from '../lib/api';
import api from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { 
  Play, Pause, RotateCcw, Coffee, Timer, Target,
  CheckCircle2, Flame, Clock, Loader2, Settings,
  FolderKanban, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';

const Focus = () => {
  const navigate = useNavigate();
  const { workspaces } = useWorkspace();
  const [mode, setMode] = useState('focus');
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  
  // Filters
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  
  // Custom durations (in minutes)
  const [customDurations, setCustomDurations] = useState({
    focus: 25,
    short_break: 5,
    long_break: 15
  });
  
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  const durations = useMemo(() => ({
    focus: customDurations.focus * 60,
    short_break: customDurations.short_break * 60,
    long_break: customDurations.long_break * 60
  }), [customDurations]);

  const modeLabels = {
    focus: 'Focus Time',
    short_break: 'Short Break',
    long_break: 'Long Break'
  };

  const fetchTasks = useCallback(async () => {
    try {
      const params = { limit: 500 };
      // Only open tasks (exclude completed)
      params.status = 'assigned,in_progress,review,blocked';
      if (workspaceFilter !== 'all') {
        params.workspace_id = workspaceFilter;
      }
      if (projectFilter !== 'all') {
        params.project_id = projectFilter;
      }
      const res = await taskApi.getAll(params);
      setTasks(res.data || []);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  }, [workspaceFilter, projectFilter]);

  const fetchData = useCallback(async () => {
    try {
      const statsRes = await focusApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Focus page load error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchTasks();
    const savedDurations = localStorage.getItem('focusDurations');
    if (savedDurations) {
      const parsed = JSON.parse(savedDurations);
      setCustomDurations(parsed);
      setTimeLeft(parsed.focus * 60);
    }
  }, [fetchData, fetchTasks]);

  // Fetch projects when workspace filter changes
  useEffect(() => {
    const fetchProjects = async () => {
      if (workspaceFilter && workspaceFilter !== 'all' && workspaceFilter !== 'personal') {
        try {
          const response = await api.get(`/workspaces/${workspaceFilter}`);
          setProjects(response.data.projects || []);
        } catch {
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
      setProjectFilter('all');
    };
    fetchProjects();
  }, [workspaceFilter]);

  // Re-fetch tasks when filters change (skip initial)
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft]);

  useEffect(() => {
    if (timeLeft === 0 && currentSession) {
      const completeSession = async () => {
        setIsRunning(false);
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        try {
          await focusApi.complete(currentSession.session_id);
          toast.success('Session complete! Great work!');
          fetchData();
        } catch (error) {
          console.error('Failed to complete session:', error);
        }
        setCurrentSession(null);
        setTimeLeft(durations[mode]);
        if (mode === 'focus') {
          toast.info('Time for a break!');
        }
      };
      completeSession();
    }
  }, [timeLeft, currentSession, mode, fetchData, durations]);

  const handleStart = async () => {
    if (!isRunning && !currentSession) {
      try {
        const response = await focusApi.start({
          task_id: selectedTaskId === 'none' ? null : selectedTaskId || null,
          duration_minutes: Math.ceil(durations[mode] / 60),
          session_type: mode
        });
        setCurrentSession(response.data);
        setIsRunning(true);
        toast.success(`${modeLabels[mode]} started! Let's go!`);
      } catch (error) {
        toast.error('Failed to start session');
      }
    } else {
      setIsRunning(true);
    }
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(durations[mode]);
    setCurrentSession(null);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setTimeLeft(durations[newMode]);
    setIsRunning(false);
    setCurrentSession(null);
  };

  const handleSaveDurations = () => {
    localStorage.setItem('focusDurations', JSON.stringify(customDurations));
    setTimeLeft(customDurations[mode] * 60);
    setShowSettings(false);
    toast.success('Custom focus times saved!');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const selectedTask = useMemo(() => {
    if (!selectedTaskId || selectedTaskId === 'none') return null;
    return tasks.find(t => t.task_id === selectedTaskId);
  }, [selectedTaskId, tasks]);

  const progress = ((durations[mode] - timeLeft) / durations[mode]) * 100;
  const circumference = 2 * Math.PI * 140;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="h-full overflow-auto">
        <div className="p-6 md:p-8 max-w-4xl mx-auto pb-20" data-testid="focus-page">
          {/* Hidden audio for notification */}
          <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleDB0mNPNrXcdGDuK" />
          
          {/* Header with Settings */}
          <div className="flex items-center justify-between mb-8">
            <div className="text-center flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Focus Mode
              </h1>
              <p className="text-muted-foreground">Boost your productivity with the Pomodoro technique</p>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full"
              onClick={() => setShowSettings(true)}
              data-testid="focus-settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>

          {/* Custom Duration Settings Dialog */}
          <Dialog open={showSettings} onOpenChange={setShowSettings}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#4A9CC7]" />
                  Custom Focus Times
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Focus Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="120"
                    value={customDurations.focus}
                    onChange={(e) => setCustomDurations({
                      ...customDurations,
                      focus: Math.max(1, Math.min(120, parseInt(e.target.value) || 25))
                    })}
                    data-testid="focus-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Short Break Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={customDurations.short_break}
                    onChange={(e) => setCustomDurations({
                      ...customDurations,
                      short_break: Math.max(1, Math.min(30, parseInt(e.target.value) || 5))
                    })}
                    data-testid="short-break-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Long Break Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={customDurations.long_break}
                    onChange={(e) => setCustomDurations({
                      ...customDurations,
                      long_break: Math.max(1, Math.min(60, parseInt(e.target.value) || 15))
                    })}
                    data-testid="long-break-duration-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSaveDurations}
                  className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                  data-testid="save-durations-btn"
                >
                  Save Settings
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Mode Selector */}
          <div className="flex justify-center gap-3 mb-8">
            <Button
              variant={mode === 'focus' ? 'default' : 'outline'}
              onClick={() => handleModeChange('focus')}
              className={`rounded-full ${mode === 'focus' ? 'bg-[#4A9CC7] hover:bg-[#3A8CB7]' : ''}`}
              data-testid="focus-mode-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              Focus ({customDurations.focus}m)
            </Button>
            <Button
              variant={mode === 'short_break' ? 'default' : 'outline'}
              onClick={() => handleModeChange('short_break')}
              className={`rounded-full ${mode === 'short_break' ? 'bg-green-500 hover:bg-green-600' : ''}`}
              data-testid="short-break-btn"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Short ({customDurations.short_break}m)
            </Button>
            <Button
              variant={mode === 'long_break' ? 'default' : 'outline'}
              onClick={() => handleModeChange('long_break')}
              className={`rounded-full ${mode === 'long_break' ? 'bg-purple-500 hover:bg-purple-600' : ''}`}
              data-testid="long-break-btn"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Long ({customDurations.long_break}m)
            </Button>
          </div>

          {/* Timer */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <svg className="transform -rotate-90" width="320" height="320">
                <circle
                  cx="160"
                  cy="160"
                  r="140"
                  fill="none"
                  className="stroke-muted"
                  strokeWidth="12"
                />
                <circle
                  cx="160"
                  cy="160"
                  r="140"
                  fill="none"
                  stroke={mode === 'focus' ? '#4A9CC7' : mode === 'short_break' ? '#22C55E' : '#A855F7'}
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="timer-ring"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="timer-display text-foreground" data-testid="timer-display">
                  {formatTime(timeLeft)}
                </span>
                <span className="text-muted-foreground mt-2">{modeLabels[mode]}</span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-4 mb-8">
            {!isRunning ? (
              <Button
                size="lg"
                onClick={handleStart}
                className="bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full px-10 py-6 text-lg"
                data-testid="start-btn"
              >
                <Play className="w-6 h-6 mr-2" />
                {currentSession ? 'Resume' : 'Start'}
              </Button>
            ) : (
              <Button
                size="lg"
                onClick={handlePause}
                variant="outline"
                className="rounded-full px-10 py-6 text-lg"
                data-testid="pause-btn"
              >
                <Pause className="w-6 h-6 mr-2" />
                Pause
              </Button>
            )}
            <Button
              size="lg"
              onClick={handleReset}
              variant="outline"
              className="rounded-full px-8 py-6"
              data-testid="reset-btn"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          </div>

          {/* Task Selection with Workspace/Project Filters */}
          {mode === 'focus' && (
            <div className="max-w-lg mx-auto mb-8 space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Link to Task
              </label>
              
              {/* Filter row */}
              <div className="flex items-center gap-2">
                <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
                  <SelectTrigger className="flex-1 rounded-xl h-9 text-xs" data-testid="focus-workspace-filter">
                    <FolderKanban className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="All Workspaces" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workspaces</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    {workspaces.filter(ws => ws.workspace_type !== 'personal').map(ws => (
                      <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={projectFilter} onValueChange={setProjectFilter} disabled={workspaceFilter === 'all' || workspaceFilter === 'personal'}>
                  <SelectTrigger className="flex-1 rounded-xl h-9 text-xs" data-testid="focus-project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(proj => (
                      <SelectItem key={proj.project_id} value={proj.project_id}>{proj.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Task dropdown */}
              <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                <SelectTrigger className="rounded-xl" data-testid="task-selector">
                  <SelectValue placeholder="Select a task to focus on" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No task</SelectItem>
                  {tasks.map(task => (
                    <SelectItem key={task.task_id} value={task.task_id}>
                      <span className="truncate">{task.title}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Link to selected task */}
              {selectedTask && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border text-sm">
                  <Target className="w-4 h-4 text-[#4A9CC7] shrink-0" />
                  <span className="truncate text-foreground font-medium">{selectedTask.title}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto shrink-0 h-7 w-7 p-0"
                    onClick={() => navigate('/tasks')}
                    data-testid="focus-go-to-task-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="text-center" data-testid="stat-sessions">
              <CardContent className="p-6">
                <Timer className="w-8 h-8 text-[#4A9CC7] mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {stats?.sessions_today || 0}
                </p>
                <p className="text-sm text-muted-foreground">Sessions Today</p>
              </CardContent>
            </Card>
            
            <Card className="text-center" data-testid="stat-focus-time">
              <CardContent className="p-6">
                <Clock className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {stats?.focus_time_today || 0}m
                </p>
                <p className="text-sm text-muted-foreground">Focus Time</p>
              </CardContent>
            </Card>
            
            <Card className="text-center" data-testid="stat-streak">
              <CardContent className="p-6">
                <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {stats?.streak_days || 0}
                </p>
                <p className="text-sm text-muted-foreground">Day Streak</p>
              </CardContent>
            </Card>
          </div>

          {/* Tips */}
          <Card className="mt-8 bg-[#4A9CC7]/10 dark:bg-[#4A9CC7]/20">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-[#4A9CC7]" />
                Pomodoro Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>- Focus for {customDurations.focus} minutes, then take a {customDurations.short_break}-minute break</li>
                <li>- After 4 focus sessions, take a longer {customDurations.long_break}-minute break</li>
                <li>- Remove all distractions during focus time</li>
                <li>- Stay hydrated and stretch during breaks</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default Focus;
